import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getApiErrorMessage,
  notifyApi,
  notificationQueries,
  queryKeys,
} from '@milliswift/api-client'
import {
  Badge,
  Button,
  EmptyState,
  PageSkeleton,
  SectionHeader,
  SegmentedControl,
  useToast,
} from '@milliswift/ui'
import { Bell, CheckCheck } from 'lucide-react'
import { useState } from 'react'
import type { AppNotification, NotificationCategory } from '@milliswift/types'

const CATEGORY_TONE: Record<NotificationCategory, 'info' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  account: 'neutral',
  security: 'danger',
  devices: 'info',
  billing: 'warning',
  org: 'neutral',
  support: 'info',
  system: 'neutral',
}

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  account: 'Account',
  security: 'Security',
  devices: 'Devices',
  billing: 'Billing',
  org: 'Organisation',
  support: 'Support',
  system: 'System',
}

/**
 * Notification preferences, backed by the real /notification-preferences
 * endpoints: per-category in-app and email toggles.
 */
export default function Notifications() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const preferences = useQuery(notificationQueries.preferences())
  const [tab, setTab] = useState('all')

  const list = useQuery(notificationQueries.list({ limit: 50 }))
  const unread = useQuery(notificationQueries.unreadCount())

  const markRead = useMutation({
    mutationFn: (id: string) => notifyApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAll = useMutation({
    mutationFn: () => notifyApi.markAllRead(),
    onSuccess: () => {
      toast.success('All caught up')
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (error) => toast.error('Could not mark all read', getApiErrorMessage(error)),
  })

  const setPreference = useMutation({
    mutationFn: (data: Parameters<typeof notifyApi.setPreference>[0]) => notifyApi.setPreference(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationPreferences })
    },
    onError: (error) => toast.error('Could not save', getApiErrorMessage(error)),
  })

  if (preferences.isPending || list.isPending) return <PageSkeleton />

  const categories: NotificationCategory[] = [
    'account',
    'security',
    'devices',
    'billing',
    'org',
    'support',
    'system',
  ]

  const items = (list.data ?? []).filter((item) =>
    tab === 'all' ? true : tab === 'unread' ? !item.read_at : item.category === tab
  )

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Notifications"
        description={`${unread.data?.unread_count ?? 0} unread`}
        action={
          <Button
            variant="outline"
            size="sm"
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}
            leftIcon={<CheckCheck className="h-3.5 w-3.5" />}
          >
            Mark all read
          </Button>
        }
      />

      {/* ---------------- delivery preferences ---------------- */}
      <section className="rounded-card border border-line bg-surface shadow-card">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">Delivery settings</h2>
          <p className="mt-0.5 text-caption text-muted">
            Per category. Security emails are always sent regardless of these settings.
          </p>
        </header>

        {preferences.isError ? (
          <p className="px-5 py-6 text-sm text-muted">
            Could not load preferences — {getApiErrorMessage(preferences.error)}
          </p>
        ) : (
          <div className="divide-y divide-line">
            {categories.map((category) => {
              const pref = (preferences.data ?? []).find((p) => p.category === category)

              return (
                <div key={category} className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <p className="text-sm font-medium text-foreground">{CATEGORY_LABEL[category]}</p>

                  <div className="flex items-center gap-5">
                    {(['in_app_enabled', 'email_enabled'] as const).map((channel) => (
                      <label
                        key={channel}
                        className="flex cursor-pointer items-center gap-2 text-caption text-muted"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-line-strong accent-[rgb(var(--ms-accent))]"
                          checked={pref?.[channel] ?? true}
                          disabled={setPreference.isPending}
                          onChange={(event) => {
                            // Both channels must be sent — the endpoint replaces
                            // the whole preference.
                            setPreference.mutate({
                              category,
                              in_app_enabled:
                                channel === 'in_app_enabled'
                                  ? event.currentTarget.checked
                                  : (pref?.in_app_enabled ?? true),
                              email_enabled:
                                channel === 'email_enabled'
                                  ? event.currentTarget.checked
                                  : (pref?.email_enabled ?? true),
                            })
                          }}
                        />
                        {channel === 'in_app_enabled' ? 'In-app' : 'Email'}
                      </label>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* ---------------- feed ---------------- */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedControl
            value={tab}
            onValueChange={setTab}
            options={[
              { value: 'all', label: 'All' },
              { value: 'unread', label: 'Unread' },
              ...categories.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
            ]}
          />
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={<Bell />}
            title="Nothing here"
            description="Device approvals, quota warnings and account events will appear here."
          />
        ) : (
          <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-surface shadow-card">
            {items.map((item: AppNotification) => (
              <li
                key={item.id}
                className={`flex items-start gap-3 px-5 py-4 ${item.read_at ? 'opacity-60' : ''}`}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{item.title}</p>
                    <Badge tone={CATEGORY_TONE[item.category]} size="sm">
                      {CATEGORY_LABEL[item.category]}
                    </Badge>
                  </div>
                  <p className="text-caption text-muted">{item.body}</p>
                  <p className="text-[11px] text-subtle">
                    {new Date(item.created_at).toLocaleString()}
                  </p>
                </div>

                {!item.read_at ? (
                  <Button size="sm" variant="ghost" onClick={() => markRead.mutate(item.id)}>
                    Mark read
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
